import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { FaArrowLeft, FaClock, FaDownload } from "react-icons/fa";
import { descargarCsv } from "./csv";
import Spinner from "./Spinner";

const fmt = (v) =>
    v === null || v === undefined ? "—" : Number(v).toLocaleString("es-MX");

export default function CpsDetalleDia() {
    const navigate = useNavigate();
    const { iAccount, fecha } = useParams();
    const location = useLocation();
    const [sp] = useSearchParams();
    const username = location.state?.username || null;
    const remoteIp =
        sp.get("ip") || location.state?.remoteIp || null;

    const [horas, setHoras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ col: "hora", dir: "asc" });

    const toggleSort = (col) =>
        setSort((s) =>
            s.col === col
                ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
                : { col, dir: "desc" }
        );
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        setError(null);
        axios
            .post(
                API_ROUTES.analisis.horas,
                {
                    i_account: Number(iAccount),
                    fecha,
                    remote_ip: remoteIp,
                },
                { timeout: 90000 }
            )
            .then((res) => {
                if (cancel) return;
                setHoras(Array.isArray(res.data?.horas) ? res.data.horas : []);
            })
            .catch((err) => {
                if (cancel) return;
                const msg =
                    err?.code === "ECONNABORTED"
                        ? "El cálculo tardó demasiado para esta cuenta/día. Suele resolverse con el índice idx_cps_cover en la tabla del mes."
                        : err?.response?.data?.message || err?.message || "Error";
                setError(msg);
                setHoras([]);
            })
            .finally(() => !cancel && setLoading(false));
        return () => {
            cancel = true;
        };
    }, [iAccount, fecha, remoteIp]);

    // El backend solo regresa horas con datos; rellenamos las 24 (0..23).
    const filas = useMemo(() => {
        const mapa = new Map(horas.map((h) => [Number(h.hora), h]));
        return Array.from({ length: 24 }, (_, hora) => {
            const h = mapa.get(hora);
            return {
                hora,
                total: h ? Number(h.total) : 0,
                contestadas: h ? Number(h.contestadas) : 0,
                no_contestadas: h ? Number(h.no_contestadas) : 0,
                cps_pico: h ? Number(h.cps_pico) : 0,
                sim_pico: h ? Number(h.sim_pico) : 0,
                vacia: !h,
            };
        });
    }, [horas]);

    // Orden visual (el CSV sigue saliendo cronologico desde `filas`).
    const filasOrdenadas = useMemo(() => {
        const arr = [...filas];
        const { col, dir } = sort;
        const factor = dir === "asc" ? 1 : -1;
        arr.sort((a, b) => (Number(a[col] || 0) - Number(b[col] || 0)) * factor);
        return arr;
    }, [filas, sort]);

    const hayDatos = horas.length > 0;

    // Genera el CSV con lo YA calculado (sin volver a pegarle al servidor).
    const exportar = () =>
        descargarCsv(
            [
                {
                    key: "hora",
                    label: "hora",
                    format: (v) => `${String(v).padStart(2, "0")}:00`,
                },
                { key: "cps_pico", label: "cps_pico" },
                { key: "sim_pico", label: "simultaneas_pico" },
                { key: "total", label: "llamadas" },
                { key: "contestadas", label: "contestadas" },
                { key: "no_contestadas", label: "no_contestadas" },
            ],
            filas,
            `cps_horas_cuenta${iAccount}${remoteIp ? "_ip" + remoteIp.replace(/[.:]/g, "-") : ""}_${fecha}`
        );

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <button
                onClick={() =>
                    navigate(
                        `/analisis/cps/${iAccount}` +
                        (remoteIp ? `?ip=${encodeURIComponent(remoteIp)}` : "")
                    )
                }
                className="text-sm text-slate-500 hover:text-cyan-700 flex items-center gap-2 mb-4"
            >
                <FaArrowLeft /> Volver a los días
            </button>

            <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <FaClock className="text-cyan-600" />
                        {fecha}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {username || `Cuenta ${iAccount}`}
                        {remoteIp && (
                            <>
                                {" · IP "}
                                <span className="font-mono text-slate-700">{remoteIp}</span>
                            </>
                        )}{" "}
                        · detalle por hora (cálculo en vivo).
                    </p>
                </div>
                {hayDatos && !loading && (
                    <button
                        onClick={exportar}
                        className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                    >
                        <FaDownload /> Descargar CSV
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500 text-left">
                            <SortTh col="hora" sort={sort} onClick={toggleSort}>Hora</SortTh>
                            <SortTh col="cps_pico" sort={sort} onClick={toggleSort} right>CPS pico</SortTh>
                            <SortTh col="sim_pico" sort={sort} onClick={toggleSort} right>Simult. pico</SortTh>
                            <SortTh col="total" sort={sort} onClick={toggleSort} right>Llamadas</SortTh>
                            <SortTh col="contestadas" sort={sort} onClick={toggleSort} right>Contestadas</SortTh>
                            <SortTh col="no_contestadas" sort={sort} onClick={toggleSort} right>No contestadas</SortTh>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6}>
                                    <Spinner label="Calculando horas…" />
                                </td>
                            </tr>
                        ) : !hayDatos ? (
                            <tr>
                                <td colSpan={6} className="text-center py-10 text-slate-400">
                                    Sin llamadas este día.
                                </td>
                            </tr>
                        ) : (
                            filasOrdenadas.map((f) => (
                                <tr
                                    key={f.hora}
                                    className={`border-t border-slate-100 ${f.vacia ? "text-slate-300" : ""
                                        }`}
                                >
                                    <Td>{String(f.hora).padStart(2, "0")}:00</Td>
                                    <Td right>{fmt(f.cps_pico)}</Td>
                                    <Td right>{fmt(f.sim_pico)}</Td>
                                    <Td right>{fmt(f.total)}</Td>
                                    <Td right>{fmt(f.contestadas)}</Td>
                                    <Td right>{fmt(f.no_contestadas)}</Td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, right }) {
    return (
        <th className={`px-3 py-2 font-medium ${right ? "text-right" : ""}`}>
            {children}
        </th>
    );
}
function SortTh({ col, sort, onClick, right, children }) {
    const active = sort.col === col;
    const arrow = !active ? "↕" : sort.dir === "asc" ? "↑" : "↓";
    return (
        <th
            onClick={() => onClick(col)}
            className={`px-3 py-2 font-medium cursor-pointer select-none hover:text-cyan-700 ${right ? "text-right" : ""
                }`}
        >
            <span className="inline-flex items-center gap-1">
                {right && <span className={active ? "text-cyan-600" : "text-slate-300"}>{arrow}</span>}
                {children}
                {!right && <span className={active ? "text-cyan-600" : "text-slate-300"}>{arrow}</span>}
            </span>
        </th>
    );
}
function Td({ children, right }) {
    return (
        <td className={`px-3 py-2 ${right ? "text-right tabular-nums" : ""}`}>
            {children}
        </td>
    );
}